import { useCallback } from "react";
import { useMutation, useQuery } from "urql";
import {
  SponsorReminderSettingQuery,
  UpsertSponsorReminderSettingMutation,
} from "../graphql/sponsorReminderSetting.graphql";

export interface SponsorReminderCondition {
  daysBeforeDeadline: number | null;
  sendDate: string | null;
  completionRateThreshold: number | null;
}

export interface SponsorReminderSetting {
  sponsorReminderSettingId: string;
  termId: string;
  title: string | null;
  description: string | null;
  enabled: boolean;
  recipientEmails: string[];
  conditions: SponsorReminderCondition[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UpsertSponsorReminderSettingInput {
  termId: string;
  title?: string | null;
  description?: string | null;
  enabled: boolean;
  recipientEmails: string[];
  conditions: SponsorReminderCondition[];
}

export function useSponsorReminderSetting(termId: string | null) {
  const [result, reexecute] = useQuery({
    query: SponsorReminderSettingQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    sponsorReminderSetting:
      (result.data?.sponsorReminderSetting as
        | SponsorReminderSetting
        | undefined) ?? null,
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useUpsertSponsorReminderSetting() {
  const [, executeUpsert] = useMutation(UpsertSponsorReminderSettingMutation);

  const upsertSponsorReminderSetting = useCallback(
    async (
      data: UpsertSponsorReminderSettingInput,
    ): Promise<SponsorReminderSetting | null> => {
      const result = await executeUpsert({ input: data });
      if (result.error) throw new Error(result.error.message);
      return (
        (result.data?.upsertSponsorReminderSetting as
          | SponsorReminderSetting
          | undefined) ?? null
      );
    },
    [executeUpsert],
  );

  return { upsertSponsorReminderSetting };
}
